import {
    ElementNode,
    $applyNodeReplacement,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    $nodesOfType,
} from 'lexical';
import { $findMatchingParent } from '@lexical/utils';
import { celebration } from '../Themes/EditorClasses';

/** How many marks one piece of content may carry. */
export const celebrationLimit = 3;

/** The attribute published content is recognised by; ContentRenderer watches for it. */
export const celebrationAttribute = 'data-celebrate';

/** Marks the run of text that celebrates when the reader scrolls to it. */
export class CelebrationNode extends ElementNode {
    static getType() {
        return 'celebration';
    }

    static clone(node) {
        return new CelebrationNode(node.__key);
    }

    createDOM() {
        const element = document.createElement('span');

        element.setAttribute(celebrationAttribute, '');
        element.className = celebration;

        return element;
    }

    updateDOM() {
        return false;
    }

    // Needed beyond paste: editors with output-format="html" store the mark as markup, so a
    // second edit of the same content would drop it.
    static importDOM() {
        return {
            span: (domNode) => domNode.hasAttribute(celebrationAttribute)
                ? { conversion: () => ({ node: $createCelebrationNode() }), priority: 1 }
                : null,
        };
    }

    // No class: the editor tint must not reach the reader, who sees the text as written.
    exportDOM() {
        const element = document.createElement('span');

        element.setAttribute(celebrationAttribute, '');

        return { element };
    }

    static importJSON() {
        return $createCelebrationNode();
    }

    exportJSON() {
        return {
            ...super.exportJSON(),
            type: 'celebration',
            version: 1,
        };
    }

    isInline() {
        return true;
    }

    // Match LinkNode: typing at the boundaries must not extend the mark.
    canInsertTextBefore() {
        return false;
    }

    canInsertTextAfter() {
        return false;
    }
}

export function $createCelebrationNode() {
    return $applyNodeReplacement(new CelebrationNode());
}

export function $isCelebrationNode(node) {
    return node instanceof CelebrationNode;
}

/** How many marks the document already carries. */
export function $countCelebrations() {
    return $nodesOfType(CelebrationNode).length;
}

export function $toggleCelebration() {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    for (const node of selection.getNodes()) {
        const existing = $findMatchingParent(node, $isCelebrationNode);
        if (existing) {
            existing.getChildren().forEach(child => existing.insertBefore(child));
            existing.remove();
            return;
        }
    }

    if (selection.isCollapsed()) return;

    if ($countCelebrations() >= celebrationLimit) return;

    // extract() splits partially-selected text nodes so we wrap exactly the selection.
    const textNodes = selection.extract().filter(node => $isTextNode(node) && node.isAttached());
    if (!textNodes.length) return;

    const parent = textNodes[0].getParent();
    if (!parent || !textNodes.every(node => node.getParent() === parent)) return;

    const wrapper = $createCelebrationNode();
    textNodes[0].insertBefore(wrapper);
    textNodes.forEach(node => wrapper.append(node));
}
